import moment from 'moment';
import { TIME_ALERTS } from 'components/Times/utils';
import { convertFromUTC } from 'utils/datetime';
import { GoAlert } from 'react-icons/go';

export const formatTime = (timeString) => {
    if (!timeString) return "-";
    return moment(timeString).format("HH:mm:ss");
};

export const formatDate = (dateString) => {
    if (!dateString) return "-";
    return moment(dateString).format("DD/MM/YYYY");
};

export const formatDateTime = (dateTimeString) => {
    if (!dateTimeString) return "-";
    return moment(dateTimeString).format("DD/MM/YYYY HH:mm:ss");
};

export const calculateDuration = (start, end) => {
    if (!start || !end) return null;
    const startMoment = moment(start);
    const endMoment = moment(end);
    return endMoment.diff(startMoment, "seconds");
};

/** Horas API en decimal (p. ej. 1.4286) → "1h 26m"; 0 → "0h". */
export const formatDecimalHoursAsHm = (decimalHours) => {
	if (decimalHours === undefined || decimalHours === null) return "—";
	const num = Number(String(decimalHours).replace(",", "."));
	if (Number.isNaN(num)) return "—";
	if (num === 0) return "0h";
	const seconds = Math.round(num * 3600);
	return formatSeconds(seconds, "hm");
};

/** Desvío de balance (short_raw en horas) → "1h 05m", "+45s", "-30s", etc. */
export const formatBalanceDeltaHmOrSeconds = (shortRawHours) => {
	if (shortRawHours === undefined || shortRawHours === null || Number.isNaN(Number(shortRawHours))) {
		return "—";
	}
	const deltaSec = Math.round(Number(shortRawHours) * 3600);
	if (deltaSec === 0) return "0";
	if (Math.abs(deltaSec) < 60) {
		return `${deltaSec > 0 ? "+" : "-"}${Math.abs(deltaSec)}s`;
	}
	return formatSeconds(deltaSec, "hm");
};

export const formatSeconds = (seconds, format = "hms") => {
    if (seconds === undefined || seconds === null) return "-";

    const absSeconds = Math.abs(seconds);
    const hours = Math.floor(absSeconds / 3600);
    const minutes = Math.floor((absSeconds % 3600) / 60);
    const secs = absSeconds % 60;
    const sign = seconds < 0 ? "-" : "";

    if (hours === 0 && minutes === 0 && format !== "hms") {
        return "-";
    }

    switch (format) {
        case "hm":
            if (hours === 0) {
                return `${sign}${minutes}m`;
            }
            if (minutes === 0) {
                return `${sign}${hours}h`;
            }
            return `${sign}${hours}h ${minutes.toString().padStart(2, "0")}m`;
        case "h'":
            if (hours === 0) {
                return `${minutes}'`;
            }
            if (minutes === 0) {
                return `${hours}h`;
            }
            return `${hours}h ${minutes}'`;
        case "m":
            return `${sign}${Math.floor(absSeconds / 60)}m`;
        default:
            return `${sign}${hours}h ${minutes.toString().padStart(2, "0")}m ${secs
                .toString()
                .padStart(2, "0")}s`;
    }
};

/**
 * Formatea los turnos planificados de un día a partir de una lista de
 * assignments (cada uno con `time_intervals`).
 *
 * Formato esperado:
 *   - 1 intervalo            → "HH:mm a HH:mm"
 *   - 2+ intervalos (turnos   → "HH:mm a HH:mm - HH:mm a HH:mm"
 *     partidos o múltiples
 *     asignaciones)
 *
 * Si no hay intervalos válidos, devuelve `""`.
 */
export const getPlannedShiftIntervals = (assignments) => {
    if (!Array.isArray(assignments) || assignments.length === 0) return [];

    const intervals = assignments
        .flatMap((a) => (Array.isArray(a?.time_intervals) ? a.time_intervals : []))
        .map((iv) => ({
            start: convertFromUTC(iv?.start),
            end: convertFromUTC(iv?.end),
        }))
        .filter((iv) => iv.start && iv.end);

    if (intervals.length === 0) return [];

    intervals.sort((a, b) => a.start.localeCompare(b.start));

    return intervals.map((iv) => `${iv.start} a ${iv.end}`);
};

export const formatPlannedShift = (assignments) => {
    const intervals = getPlannedShiftIntervals(assignments);
    if (!intervals?.length) return "";
    return intervals.join(" - ");
};

export const getTooltipAlertContent = (alerts, t = (k) => k) => {
    return alerts.map((alert, i) => {
        const plan_time = convertFromUTC(alert.plan_time);
        return (
            <li key={i}>
                <GoAlert className="tooltip-icon icon-alert" />{" "}
                {TIME_ALERTS[alert.type] || alert.description || alert.type || t('attendance.noAlertDesc')}
                {alert.plan_time && <>. {t('times.alerts.planned')}: {plan_time}</>}
            </li>
        );
    });
}; 